import React from "react";
import { getCorrectOptionNumber } from "./qmsQuestionImportUtils";

const focusInputEnd = (event) => {
  const el = event.target;
  const len = el.value.length;
  requestAnimationFrame(() => {
    el.setSelectionRange(len, len);
  });
};

const ImportGridInput = ({ value, placeholder, onChange, className = "" }) => (
  <input
    type="text"
    className={className}
    value={value}
    placeholder={placeholder}
    onChange={onChange}
    onFocus={focusInputEnd}
    onClick={focusInputEnd}
  />
);

const ImportCorrectInput = ({ value, onChange }) => {
  const handleChange = (event) => {
    const digits = event.target.value.replace(/\D/g, "");
    if (!digits) {
      onChange(null);
      return;
    }
    const num = parseInt(digits.slice(-1), 10);
    if (num >= 1 && num <= 4) onChange(num);
  };

  const displayValue =
    value === "" || value === null || value === undefined ? "" : String(value);

  return (
    <input
      type="text"
      inputMode="numeric"
      className="qms-import-grid__correct-input"
      maxLength={1}
      value={displayValue}
      onChange={handleChange}
      onFocus={focusInputEnd}
      onClick={focusInputEnd}
    />
  );
};

const QmsQuestionImportSheet = ({
  drafts,
  errorsById = {},
  fileName,
  parseErrors = [],
  importInputRef,
  onDownloadTemplate,
  onFileChange,
  onUpdateDraft,
  onRemoveDraft,
  onCorrectOptionChange,
}) => (
  <div className="qms-import-sheet">
    <div className="qms-import-sheet__toolbar">
      <div className="qms-import-sheet__toolbar-left">
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={onDownloadTemplate}
        >
          <i className="fas fa-download me-1" aria-hidden="true" />
          Download template
        </button>
        <label className="btn btn-outline-primary btn-sm mb-0">
          <i className="fas fa-upload me-1" aria-hidden="true" />
          Choose file
          <input
            ref={importInputRef}
            type="file"
            accept=".xlsx,.csv"
            className="d-none"
            onChange={onFileChange}
          />
        </label>
        {fileName && <span className="qms-import-sheet__filename">{fileName}</span>}
      </div>
      {drafts.length > 0 && (
        <span className="qms-import-sheet__count">
          {drafts.length} question(s) · edit inline before import
        </span>
      )}
    </div>

    {parseErrors.length > 0 && (
      <div className="alert alert-warning py-2 mb-2">
        {parseErrors.slice(0, 8).map((msg) => (
          <div key={msg}>{msg}</div>
        ))}
        {parseErrors.length > 8 && (
          <div>…and {parseErrors.length - 8} more issue(s).</div>
        )}
      </div>
    )}

    {drafts.length === 0 ? (
      <div className="qms-import-sheet__empty">
        Upload an Excel or CSV file using the template columns: Question, Option1–Option4,
        CorrectOption (1–4).
      </div>
    ) : (
      <div className="qms-import-grid-wrap">
        <table className="qms-import-grid">
          <thead>
            <tr>
              <th className="qms-import-grid__col-num">#</th>
              <th className="qms-import-grid__col-question">Question</th>
              <th>Option 1</th>
              <th>Option 2</th>
              <th>Option 3</th>
              <th>Option 4</th>
              <th className="qms-import-grid__col-correct">Correct</th>
              <th className="qms-import-grid__col-actions" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {drafts.map((draft, idx) => {
              const rowError = errorsById[draft.id];
              return (
                <React.Fragment key={draft.id}>
                  <tr className={rowError ? "qms-import-grid__row--error" : ""}>
                    <td className="qms-import-grid__num">{idx + 1}</td>
                    <td className="qms-import-grid__cell-question">
                      <ImportGridInput
                        value={draft.questionText}
                        placeholder="Question"
                        onChange={(e) =>
                          onUpdateDraft(draft.id, (d) => ({
                            ...d,
                            questionText: e.target.value,
                          }))
                        }
                      />
                    </td>
                    {draft.options.map((opt, optIdx) => (
                      <td key={optIdx} className="qms-import-grid__cell-option">
                        <ImportGridInput
                          value={opt.OptionText}
                          placeholder={`Option ${optIdx + 1}`}
                          onChange={(e) =>
                            onUpdateDraft(draft.id, (d) => ({
                              ...d,
                              options: d.options.map((o, i) =>
                                i === optIdx ? { ...o, OptionText: e.target.value } : o,
                              ),
                            }))
                          }
                        />
                      </td>
                    ))}
                    <td className="qms-import-grid__correct">
                      <ImportCorrectInput
                        value={getCorrectOptionNumber(draft)}
                        onChange={(num) => onCorrectOptionChange(draft.id, num)}
                      />
                    </td>
                    <td className="qms-import-grid__actions">
                      <button
                        type="button"
                        className="btn btn-link btn-sm text-danger p-0"
                        onClick={() => onRemoveDraft(draft.id)}
                        title="Remove row"
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                  {rowError && (
                    <tr className="qms-import-grid__error-row">
                      <td colSpan={8}>{rowError}</td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export default QmsQuestionImportSheet;
